import { useMemo, useState } from 'react'

// off / on-idle / armed — holds the page's cyan theme.
const STATE_COLOR = ['#eceff1', '#b3e5fc', '#0288d1']
const pct = (v) => `${Math.round((v || 0) * 100)}%`

export const UtilizationTimeline = ({ data }) => {
  const [sortKey, setSortKey] = useState('uptime_pct')
  const [asc, setAsc] = useState(false)
  const units = data?.units || []

  const sorted = useMemo(() => {
    const arr = [...units]
    arr.sort((a, b) => {
      let av, bv
      if (sortKey === 'name') {
        av = (a.vehicle_name || a.serial || '').toLowerCase()
        bv = (b.vehicle_name || b.serial || '').toLowerCase()
      } else { av = a[sortKey] || 0; bv = b[sortKey] || 0 }
      if (av < bv) return asc ? -1 : 1
      if (av > bv) return asc ? 1 : -1
      return 0
    })
    return arr
  }, [units, sortKey, asc])

  const axisTicks = useMemo(() => {
    const r = data?.range
    if (!r) return []
    // range.start/end are full ISO bounds (e.g. "2026-06-12T04:00:00.000Z"). Parse
    // directly — appending a time suffix ("...ZT00:00:00") yielded Invalid Date ticks.
    const s = new Date(r.start)
    const e = new Date(r.end)
    if (isNaN(s) || isNaN(e)) return []
    const span = e - s
    const days = span / 86400000
    const fmt = (d) => days <= 2
      ? d.toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit' })
      : d.toLocaleDateString([], { month: 'numeric', day: 'numeric' })
    if (days <= 2) return [0, 0.2, 0.4, 0.6, 0.8, 1].map(f => fmt(new Date(s.getTime() + span * f)))
    // Ticks at LOCAL DAY BOUNDARIES (midnight). N days → N+1 boundaries, spread evenly =
    // exact day edges, so each m/d label marks the START (00:00 local) of that date.
    const labels = []
    const cur = new Date(s); cur.setHours(0, 0, 0, 0)
    const step = Math.max(1, Math.ceil(days / 14))
    while (cur <= e) { labels.push(fmt(cur)); cur.setDate(cur.getDate() + step) }
    labels.push(fmt(cur))   // closing boundary (00:00 of the day after the range)
    return labels
  }, [data])

  if (!units.length) return <p className="tm-empty">No utilization data for the selected units/range.</p>

  const axisRow = (
    <div className="tm-util-axis">
      <div className="tm-util-label" />
      <div className="tm-util-ticks">{axisTicks.map((t, i) => <span key={i}>{t}</span>)}</div>
      <div className="tm-util-stats" />
    </div>
  )

  const setSort = (k) => { if (sortKey === k) setAsc(!asc); else { setSortKey(k); setAsc(k === 'name') } }
  const arrow = (k) => (sortKey === k ? (asc ? ' ▲' : ' ▼') : '')

  return (
    <div className="tm-util">
      <div className="tm-util-controls">
        <span>Sort:</span>
        <button className={sortKey === 'uptime_pct' ? 'active' : ''} onClick={() => setSort('uptime_pct')}>Uptime{arrow('uptime_pct')}</button>
        <button className={sortKey === 'armed_pct' ? 'active' : ''} onClick={() => setSort('armed_pct')}>Armed{arrow('armed_pct')}</button>
        <button className={sortKey === 'name' ? 'active' : ''} onClick={() => setSort('name')}>Name{arrow('name')}</button>
      </div>
      {data.partial && <div className="tm-banner">Some units could not be loaded: {data.failed_units.join(', ')}</div>}
      <div className="tm-util-rows">
        {sorted.map(u => (
          <div className="tm-util-row" key={u.serial}>
            <div className="tm-util-label" title={u.serial}>{u.vehicle_name || u.serial}</div>
            <div className="tm-util-bar">
              {(u.slots || []).map((s, i) => <span key={i} className="tm-util-slot" style={{ background: STATE_COLOR[s] || STATE_COLOR[0] }} />)}
            </div>
            <div className="tm-util-stats"><b>{pct(u.uptime_pct)}</b> on · {pct(u.armed_pct)} armed</div>
          </div>
        ))}
      </div>
      {axisRow}
      <div className="tm-util-legend">
        <span><i style={{ background: STATE_COLOR[0] }} /> off</span>
        <span><i style={{ background: STATE_COLOR[1] }} /> on (idle)</span>
        <span><i style={{ background: STATE_COLOR[2] }} /> armed</span>
      </div>
    </div>
  )
}
